import React, { useState } from "react";
import {
  Container,
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import ordersService from "../services/ordersService";

interface UploadError {
  row: number;
  message: string;
  rawRow: Record<string, unknown>;
}

interface DuplicateRow {
  row: number;
  data: {
    riderPublicRiderId: string;
    clientId: string;
    clientName: string;
    city: string;
    date: string | Date;
    totalOrdersDay: number;
  };
}

interface UploadResponse {
  message: string;
  success?: boolean;
  insertedCount?: number;
  skippedCount?: number;
  totalRows?: number;
  count?: number;
  affectedRiders?: string[];
  errors?: UploadError[];
  validRowsCount?: number;
  duplicates?: DuplicateRow[];
  duplicateCount?: number;
}

const OrderBulkUpload: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(
    null
  );
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [_jobId, _setJobId] = useState<string | null>(null);
  const [_pollingIntervalId, _setPollingIntervalId] = useState<number | null>(null);

  // ============================================================
  // DOWNLOAD TEMPLATE
  // ============================================================
  const handleDownloadTemplate = () => {
    // Sample template data
    const templateData = [
      {
        rider_id: "RIDER001",
        client_id: "CLIENT001",
        client_name: "ABC Delivery Services",
        city: "Bangalore",
        date: "2025-12-11",
        total_orders_day: 45,
      },
      {
        rider_id: "RIDER002",
        client_id: "CLIENT002",
        client_name: "XYZ Logistics",
        city: "Mumbai",
        date: "2025-12-11",
        total_orders_day: 52,
      },
      {
        rider_id: "RIDER003",
        client_id: "CLIENT001",
        client_name: "ABC Delivery Services",
        city: "Delhi",
        date: "2025-12-11",
        total_orders_day: 38,
      },
    ];

    // Convert to CSV
    const headers = Object.keys(templateData[0]);
    const csvContent = [
      headers.join(","),
      ...templateData.map((row) =>
        headers.map((header) => {
          const value = row[header as keyof typeof row];
          // Escape quotes and wrap in quotes if needed
          if (typeof value === "string" && value.includes(",")) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "orders-template.csv";
    link.click();
    window.URL.revokeObjectURL(url);

    enqueueSnackbar("Template downloaded successfully", {
      variant: "success",
    });
  };

  // ============================================================
  // DRAG & DROP HANDLERS
  // ============================================================
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      validateAndSetFile(droppedFile);
    }
  };

  // ============================================================
  // FILE INPUT HANDLER
  // ============================================================
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    const validExtensions = [".csv", ".xlsx", ".xls"];
    const fileExtension = selectedFile.name
      .substring(selectedFile.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      enqueueSnackbar(
        "Invalid file format. Please upload CSV or Excel file.",
        {
          variant: "error",
        }
      );
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      // 5MB limit
      enqueueSnackbar("File size exceeds 5MB limit", {
        variant: "error",
      });
      return;
    }

    setFile(selectedFile);
    setUploadResponse(null);
    enqueueSnackbar(`File selected: ${selectedFile.name}`, {
      variant: "info",
    });
  };

  // ============================================================
  // UPLOAD HANDLER
  // ============================================================
  const handleUpload = async () => {
    if (!file) {
      enqueueSnackbar("Please select a file first", {
        variant: "warning",
      });
      return;
    }

    setLoading(true);
    setProgressPercent(0);

    try {
      // Use background processing to get progress updates
      const start = await ordersService.uploadOrders(file, true);
      if (start?.jobId) {
        _setJobId(start.jobId);

        // Poll status every 1s
        const id = window.setInterval(async () => {
          try {
            const status = await ordersService.getUploadStatus(start.jobId);
            setProgressPercent(status.percent || 0);

            // When done or failed, stop polling and show result
            if (status.status === "done" || status.status === "failed") {
              window.clearInterval(id);
              _setPollingIntervalId(null);
              _setJobId(null);
              setLoading(false);

              if (status.status === "done") {
                // Map job.result into uploadResponse shape expected by UI
                const res: any = {
                  message: status.result?.message || "Upload completed",
                  insertedCount: status.result?.insertedCount || 0,
                  skippedCount: status.result?.duplicateCount || 0,
                  duplicates: status.result?.duplicates || [],
                    totalRows: status.total || 0,
                    errors: status.result?.errors || [],
                };
                setUploadResponse(res);
                // Show snackbars for duplicates/errors to make messages explicit
                if (res.errors && res.errors.length > 0) {
                  enqueueSnackbar(`Upload completed with ${res.errors.length} error(s)`, { variant: "warning" });
                }

                if (res.duplicates && res.duplicates.length > 0) {
                  if (res.insertedCount && res.insertedCount > 0) {
                    enqueueSnackbar(`${res.insertedCount} orders imported, ${res.skippedCount} were duplicates`, { variant: "warning" });
                  } else {
                    enqueueSnackbar("All rows already exist in database", { variant: "error" });
                  }
                }

                if ((res.errors && res.errors.length > 0) || (res.duplicates && res.duplicates.length > 0)) setShowErrorDialog(true);
                else {
                  setShowSuccessDialog(true);
                  if (res.insertedCount && res.insertedCount > 0) {
                    enqueueSnackbar(`Successfully uploaded ${res.insertedCount} orders`, { variant: "success" });
                    setFile(null);
                  }
                }
              } else {
                enqueueSnackbar("Upload job failed: " + (status.error || status.message || ""), { variant: "error" });
                setUploadResponse(null);
              }
            }
          } catch (err: any) {
            console.error("Error polling upload status:", err.message || err);
          }
        }, 1000);

        _setPollingIntervalId(id);
      } else {
        // Fallback to synchronous response
        const response = start;
        setUploadResponse(response);
        // Show snackbars for errors/duplicates
        if (response.errors && response.errors.length > 0) {
          setShowErrorDialog(true);
          enqueueSnackbar(`Upload completed with ${response.errors.length} error(s)`, { variant: "warning" });
        }

        if (response.duplicates && response.duplicates.length > 0) {
          if (response.insertedCount && response.insertedCount > 0) {
            enqueueSnackbar(`${response.insertedCount} orders imported, ${response.skippedCount} were duplicates`, { variant: "warning" });
          } else {
            enqueueSnackbar("All rows already exist in database", { variant: "error" });
          }
        }

        if ((response.errors && response.errors.length > 0) || (response.duplicates && response.duplicates.length > 0)) {
          setShowErrorDialog(true);
        } else if (response.insertedCount && response.insertedCount > 0) {
          setShowSuccessDialog(true);
          enqueueSnackbar(`Successfully uploaded ${response.insertedCount} orders`, { variant: "success" });
          setFile(null);
        }
        setLoading(false);
      }
    } catch (error: any) {
      // If server returned structured validation data, show it in the UI
      if (error.responseData) {
        setUploadResponse(error.responseData);
          // Show errors if present
          if (error.responseData.errors && error.responseData.errors.length > 0) {
            setShowErrorDialog(true);
            enqueueSnackbar(`Upload completed with ${error.responseData.errors.length} error(s)`, { variant: "warning" });
          }

          // Show duplicates info if present
          if (error.responseData.duplicates && error.responseData.duplicates.length > 0) {
            if (error.responseData.insertedCount && error.responseData.insertedCount > 0) {
              enqueueSnackbar(`${error.responseData.insertedCount} orders imported, ${error.responseData.duplicateCount || error.responseData.skippedCount} were duplicates`, { variant: "warning" });
            } else {
              enqueueSnackbar("All rows already exist in database", { variant: "error" });
            }
          }

          if (!error.responseData.errors && !(error.responseData.duplicates && error.responseData.duplicates.length > 0)) {
            enqueueSnackbar(error.message || "Upload failed", { variant: "error" });
          }
      } else {
        enqueueSnackbar(error.message || "Upload failed", {
          variant: "error",
        });
      }
      setLoading(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setUploadResponse(null);
  };

  // ============================================================
  // RENDER ERROR TABLE
  // ============================================================
  const renderErrorTable = () => {
    if (!uploadResponse?.errors || uploadResponse.errors.length === 0) {
      return null;
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead sx={{ }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uploadResponse.errors.map((error, idx) => (
              <TableRow key={idx} sx={{ backgroundColor: idx % 2 ? "#fff" : "#fafafa" }}>
                <TableCell>
                  <Chip label={error.row} size="small" color="error" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                    {error.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    {JSON.stringify(error.rawRow).substring(0, 100)}...
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ============================================================
  // RENDER DUPLICATE TABLE
  // ============================================================
  const renderDuplicateTable = () => {
    if (!uploadResponse?.duplicates || uploadResponse.duplicates.length === 0) {
      return null;
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead sx={{  }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Rider ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Orders</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uploadResponse.duplicates?.map((dup, idx) => (
              <TableRow key={idx} sx={{ backgroundColor: idx % 2 ? "#fff" : "#fafafa" }}>
                <TableCell>
                  <Chip label={dup.row} size="small" color="warning" />
                </TableCell>
                <TableCell>{dup.data.riderPublicRiderId}</TableCell>
                <TableCell>{dup.data.clientName}</TableCell>
                <TableCell>{dup.data.city}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(dup.data.date).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>{dup.data.totalOrdersDay}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Safe computed insertion count used in multiple places to simplify JSX
  const insertedCountSafe = uploadResponse ? (uploadResponse.insertedCount || uploadResponse.count || 0) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Bulk Upload Orders
        </Typography>
        <Typography variant="body2" sx={{ color: "#666" }}>
          Upload multiple orders at once using a CSV or Excel file. Download
          the template to get started.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column: Upload Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Upload File"
              subheader="CSV or Excel format (Max 5MB)"
            />
            <CardContent>
              {/* Download Template Button */}
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  fullWidth
                  sx={{ textTransform: "none" }}
                >
                  Download Template
                </Button>
                <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                  Download the template CSV to see the required format
                </Typography>
              </Box>

              {/* Drag & Drop Zone */}
              <Box
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragging ? "#1976d2" : "#ccc",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#1976d2",
                  },
                }}
              >
                <CloudUploadIcon
                  sx={{ fontSize: 48, color: "#1976d2", mb: 1 }}
                />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Drag and drop your file here
                </Typography>
                <Typography variant="body2" sx={{ color: "#999", mb: 2 }}>
                  or click to select a file
                </Typography>

                <input
                  type="file"
                  id="file-input"
                  onChange={handleFileChange}
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                />
                <label htmlFor="file-input" style={{ cursor: "pointer" }}>
                  <Button
                    variant="contained"
                    component="span"
                    disabled={loading}
                  >
                    Select File
                  </Button>
                </label>
              </Box>

              {/* Selected File Info */}
              {file && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                   
                    borderRadius: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ✓ {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Size: {(file.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={handleClearFile}
                    disabled={loading}
                  >
                    Remove
                  </Button>
                </Box>
              )}

              {/* Upload Button */}
              <Box sx={{ mt: 3, display: "flex", gap: 2, flexDirection: "column" }}>
                {loading && (
                  <Box sx={{ width: "100%", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <div style={{ width: "100%" }}>
                          <div style={{ height: 8, background: '#eee', borderRadius: 4 }}>
                            <div style={{ width: `${progressPercent}%`, height: 8, background: '#1976d2', borderRadius: 4 }} />
                          </div>
                        </div>
                      </Box>
                      <Typography variant="caption">{progressPercent}%</Typography>
                    </Box>
                  </Box>
                )}

              <Box sx={{ mt: 0, display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={!file || loading}
                  fullWidth
                  sx={{
                    py: 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Uploading...
                    </>
                  ) : (
                    "Upload Orders"
                  )}
                </Button>
              </Box>
            </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Instructions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Instructions" />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    1. Download Template
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Click the "Download Template" button to get a sample CSV
                    file with the correct format.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    2. Fill in Your Data
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Add your order data following the template format:
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                    
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      overflow: "auto",
                    }}
                  >
                    {`rider_id, client_id, client_name,\ncity, date, total_orders_day`}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    3. Upload File
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Select your CSV/Excel file and click "Upload Orders".
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Requirements
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Rider ID must exist in system
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Date format: YYYY-MM-DD
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        All fields are required
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        File size max: 5MB
                      </Typography>
                    </li>
                  </ul>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Response */}
        {uploadResponse && (
          <Grid item xs={12}>
            {uploadResponse.errors && uploadResponse.errors.length > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ⚠ Upload completed with errors
                </Typography>
                <Typography variant="caption">
                  {uploadResponse.validRowsCount} valid rows imported out of{" "}
                  {uploadResponse.totalRows} rows.
                </Typography>
              </Alert>
            ) : uploadResponse.skippedCount && uploadResponse.skippedCount > 0 ? (
              <Alert severity={uploadResponse.insertedCount && uploadResponse.insertedCount > 0 ? "warning" : "error"} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {uploadResponse.insertedCount && uploadResponse.insertedCount > 0 
                    ? "⚠ Partial upload: Some duplicates found" 
                    : "✗ All rows are duplicates"}
                </Typography>
                <Typography variant="caption">
                  {uploadResponse.message}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ✓ Upload successful!
                </Typography>
                <Typography variant="caption">
                  {insertedCountSafe} orders imported successfully.
                </Typography>
              </Alert>
            )}

            {/* Error Details */}
            {renderErrorTable()}

            {/* Duplicate Details */}
            {uploadResponse.duplicates && uploadResponse.duplicates.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#ff9800" }}>
                  Duplicate Rows ({uploadResponse.duplicates.length})
                </Typography>
                {renderDuplicateTable()}
              </Box>
            )}

            {/* Success Summary */}
            {insertedCountSafe > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: "center", p: 2 }}>
                        <CheckCircleIcon
                          sx={{ fontSize: 40, color: "#4caf50", mb: 1 }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {insertedCountSafe}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666" }}>
                          Orders Imported
                        </Typography>
                      </Box>
                    </Grid>
                    {uploadResponse.skippedCount && uploadResponse.skippedCount > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff9800" }}>
                            {uploadResponse.skippedCount}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            Duplicates Skipped
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {uploadResponse.affectedRiders && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {uploadResponse.affectedRiders.length}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            Riders Updated
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>
        )}
      </Grid>

      {/* Error Dialog */}
      <Dialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#ffebee" }}>
          <ErrorIcon sx={{ color: "#d32f2f" }} />
          Upload Errors
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {renderErrorTable()}
          </Box>
          {uploadResponse?.duplicates && uploadResponse.duplicates.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#ff9800" }}>
                Duplicate Rows ({uploadResponse.duplicates.length})
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {renderDuplicateTable()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon sx={{ color: "#4caf50" }} />
          Upload Completed
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {uploadResponse?.insertedCount || uploadResponse?.count || 0} Orders Imported
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              {uploadResponse?.message || "Upload completed successfully"}
            </Typography>
            {uploadResponse?.skippedCount && uploadResponse.skippedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {uploadResponse.skippedCount} duplicate rows were skipped
                </Typography>
              </Alert>
            )}
            {uploadResponse?.affectedRiders && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Affected Riders:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {uploadResponse.affectedRiders.map((rider) => (
                    <Chip
                      key={rider}
                      label={rider}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowSuccessDialog(false)}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderBulkUpload;
